import { clearActiveBrandTenant } from "@/lib/brand-cache";
// src/lib/auth-client.ts
//
// Client do Better Auth (front). Fala com o back em ${baseURL}/api/auth/*. Reusa
// VITE_API_URL — mesmo back da camada de chamada (src/api.ts). organizationClient
// expõe lista/org ativa + setActive (troca de organização pela sidebar).
// oauthProviderClient: quando o login acontece dentro de um authorize OAuth
// (cliente MCP, ex.: Claude Desktop), anexa a query assinada da página
// (`oauth_query`) aos POSTs, e o back retoma o authorize assim que a sessão abre.
import { createAuthClient } from "better-auth/react";
import { organizationClient, twoFactorClient } from "better-auth/client/plugins";
import { oauthProviderClient } from "@better-auth/oauth-provider/client";
import { isBrandSubdomainHost, resolveApiBaseUrl } from "@/lib/api-base-url";
import { queryClient, SESSION_STALE_TIME_MS } from "@/lib/query-client";
import { usePreferencesStore } from "@/preferences-store";
import { clearTempPassword } from "@/lib/pending-temp-password";

export const authClient = createAuthClient({
  baseURL: resolveApiBaseUrl(),
  sessionOptions: { refetchInterval: SESSION_STALE_TIME_MS / 1000 },
  // twoFactorClient sem onTwoFactorRedirect: o login trata o
  // `twoFactorRedirect` na própria tela (passo do código), sem trocar de rota.
  plugins: [organizationClient(), twoFactorClient(), oauthProviderClient()],
});

// Dados públicos do app que pede acesso (tela de consentimento). Exige sessão.
export interface OAuthPublicClient {
  client_id: string;
  client_name?: string;
  client_uri?: string;
  logo_uri?: string;
}

export function getOAuthPublicClient(clientId: string) {
  return authClient.$fetch<OAuthPublicClient>("/oauth2/public-client", {
    method: "GET",
    query: { client_id: clientId },
  });
}

// Aceita ou recusa o pedido de acesso. O oauthProviderClient anexa a query
// assinada da página; o back responde `{ redirect: true, url }` nos dois casos
// (código de autorização ou access_denied para o app).
export function getOAuthOrganizations() {
  return authClient.$fetch<Array<{ id: string; name: string }>>("/oauth2/organizations", {
    method: "GET",
  });
}

export function answerOAuthConsent(accept: boolean, organizationId?: string) {
  return authClient.$fetch<{ redirect: boolean; url: string }>("/oauth2/consent", {
    method: "POST",
    body: { accept, ...(accept ? { organizationId } : {}) },
  });
}

// Só o que o back atende. Ficam de fora, de propósito, para o uso virar erro
// de COMPILAÇÃO e não um 403 em produção:
// - signUp: o cadastro público responde 403 SIGN_UP_DISABLED (back#194); a
//   conta nasce no convite (registerWithInvite);
// - useActiveOrganization: chama /organization/get-full-organization, fechada
//   pela lista de rotas permitidas do plugin (back#184).
export const {
  signIn,
  signOut,
  useSession,
  useListOrganizations,
  requestPasswordReset,
  resetPassword,
  changePassword,
} = authClient;

// A guarda e useSession compartilham a consulta do SDK. Um getSession avulso
// não alimenta esse estado e faz o menu consultar a mesma sessão ao montar.
let confirmedAuthState: ReturnType<typeof authClient.$store.atoms.session.get> | undefined;
let confirmedAuthAt = 0;
let authRefresh: Promise<ReturnType<typeof authClient.$store.atoms.session.get>> | undefined;

export async function refreshAuthSession(maxAgeMs = 0) {
  const session = authClient.$store.atoms.session;
  const current = session.get();
  // Reutiliza apenas a resposta confirmada por esta função. Mudanças do SDK
  // (logout, outra aba, troca de marca, erro ou refetch) invalidam a referência.
  if (
    maxAgeMs > 0 &&
    current === confirmedAuthState &&
    current.data?.session &&
    !current.error &&
    !current.isPending &&
    !current.isRefetching &&
    current.data.session.expiresAt.getTime() > Date.now() &&
    Date.now() - confirmedAuthAt < maxAgeMs
  ) {
    return current;
  }
  // O SDK cancela a chamada anterior ao receber outro refetch; compartilha a
  // chamada em andamento entre preload, guarda e navegação rápida.
  if (!authRefresh) {
    authRefresh = (async () => {
      await current.refetch();
      confirmedAuthState = session.get();
      confirmedAuthAt = Date.now();
      return confirmedAuthState;
    })().finally(() => {
      authRefresh = undefined;
    });
  }
  return authRefresh;
}

// Logout: encerra a sessão no back e ZERA o cache do Query (senão dados do
// usuário anterior vazariam para a próxima sessão na mesma aba). clear() não
// dispara refetch (que falharia com 401 pós-logout). Usar em todo botão "Sair".
export async function logout(): Promise<void> {
  confirmedAuthState = undefined;
  await signOut();
  clearTempPassword();
  usePreferencesStore.getState().setViewAs(null);
  clearActiveBrandTenant();
  queryClient.clear();
}

// Garante uma organização ativa logo após o login: o plugin cria a sessão com
// activeOrganizationId = null, o que deixa o back sem tenant nas chamadas de
// domínio. Se ainda não há org ativa, ativa a primeira filiação. Best-effort.
// false = a conta não tem nenhuma marca (ex.: criada pelo Google, back#256).
// No subdomínio de uma marca (#350) o back já abre a sessão nessa marca quando
// a pessoa é membro; sem ela, a resposta é "sem acesso" — nunca outra marca.
export async function ensureActiveOrganization(
  client: Pick<typeof authClient, "getSession" | "organization"> = authClient,
  hostname = globalThis.location?.hostname ?? "",
): Promise<boolean> {
  try {
    const { data: session } = await client.getSession();
    if (session?.session.activeOrganizationId) return true;
    if (isBrandSubdomainHost(hostname)) return false;
    const { data: orgs } = await client.organization.list();
    const first = orgs?.[0];
    if (!first) return false;
    const active = await client.organization.setActive({ organizationId: first.id });
    return !active.error;
  } catch {
    // Falha silenciosa: não bloqueia o login pela org padrão.
    return true;
  }
}

// Login com Google (back#256). O back anuncia os provedores configurados; sem
// credenciais no ambiente a lista vem vazia e o botão não aparece.
export const socialProvidersQueryKey = ["auth", "social-providers"] as const;
export interface SocialLogin {
  providers: string[];
  // Endereço principal, onde o Google aceita o retorno. No subdomínio de marca o
  // login passa por ele e a sessão volta pelo repasse (/auth/google/complete).
  primaryOrigin: string | null;
}
export async function getSocialLogin(): Promise<SocialLogin> {
  const { data, error } = await authClient.$fetch<{ providers: string[]; primaryOrigin?: string }>(
    "/social-providers",
    { method: "GET" },
  );
  if (error) throw error;
  return { providers: data?.providers ?? [], primaryOrigin: data?.primaryOrigin ?? null };
}
export async function getSocialProviders(): Promise<string[]> {
  return (await getSocialLogin()).providers;
}

// Repasse da sessão ao subdomínio de marca (back: auth/subdomain-handoff.ts).
export async function startSubdomainHandoff(target: string): Promise<string> {
  const { data, error } = await authClient.$fetch<{ url: string }>("/subdomain-handoff/start", {
    method: "POST",
    body: { target },
  });
  if (error || !data?.url) throw error ?? new Error("handoff sem URL");
  return data.url;
}
export async function isHandoffTarget(origin: string): Promise<boolean> {
  const { data } = await authClient.$fetch<{ trusted: boolean }>("/subdomain-handoff/target", {
    method: "GET",
    query: { origin },
  });
  return data?.trusted === true;
}
export async function completeSubdomainHandoff(token: string) {
  return authClient.$fetch<{ ok: boolean }>("/subdomain-handoff/complete", {
    method: "POST",
    body: { token },
  });
}

// Sai do app para o Google. O back volta para `returnPath` com a sessão aberta,
// ou para `errorPath?error=<código>` (cancelamento, conta não vinculada…).
export async function signInWithGoogle(returnPath: string, errorPath: string) {
  const origin = window.location.origin;
  return signIn.social({
    provider: "google",
    callbackURL: `${origin}${returnPath}`,
    newUserCallbackURL: `${origin}${returnPath}`,
    errorCallbackURL: `${origin}${errorPath}`,
  });
}
